import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subscription, combineLatest, filter, map, take } from 'rxjs';

import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { MentionSearchService } from '../../../Monitor/Core/services/mention-search.service';
import { MonitoringDashboardSearchService } from '../../../Monitor/Core/services/monitoring-dashboard-search.service';
import { MonitoringRoutingService } from '../../../Monitor/Core/services/monitoring-routing.service';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { AgentScopeModel, applyAgentScope } from '../../models/agent-scope.util';

/**
 * One agent's analytics: the shared monitoring analytics dashboard, narrowed to the mentions this
 * agent has acted on. The dashboard decides what it shows from the current route, which reports the
 * agent's analytics section as saved coverage analytics.
 */
@Component({
    selector: 'agpr-agent-analytics',
    templateUrl: './agent-analytics.component.html',
    styleUrls: ['./agent-analytics.component.scss'],
    standalone: false
})
export class AgentAnalyticsComponent implements OnInit, AfterViewInit, OnDestroy {

    /** The window this component put on the dashboard, so its echo is not sent back out */
    private appliedRange: DateQueryModel;
    private readonly subscriptions: Subscription[] = [];

    constructor(
        private route: ActivatedRoute,
        private mentionSearchService: MentionSearchService,
        private routingService: MonitoringRoutingService,
        private dashboardSearchService: MonitoringDashboardSearchService,
        private curationAgentsService: CurationAgentsService
    ) {}

    ngOnInit(): void {
        // The routing service only listens for NavigationEnd, and may well have been constructed by
        // this component - after the one that brought us here. The dashboard below reads the route
        // on its own init, so publish it first.
        this.routingService.syncCurrentRoute();

        // The dashboard takes its window from the search service's date query, not from the search
        // criteria, and an account search is otherwise left on All. This runs before the dashboard
        // in the template exists, so it is in place for its first read and nothing has to be
        // corrected afterwards. It also runs after the previous section has been destroyed, unlike
        // a resolver - pushing the search service while that one was still subscribed is what
        // brought the renderer down.
        this.curationAgentsService.selectedDateRange$.pipe(take(1)).subscribe(dateRange => {
            if (dateRange) {
                this.appliedRange = dateRange;
                this.dashboardSearchService.setDateQuery(dateRange);
            }
        });

        // Switching agent, or ticking sources and ad hoc runs, re-scopes the dashboard. The criteria
        // is watched alongside them because the resolver's is not necessarily the one the dashboard
        // ends up on - it builds its own as it starts up, and that one arrives without the scope.
        this.subscriptions.push(
            combineLatest([
                this.route.parent.paramMap.pipe(
                    map(params => Number(params.get('agent_id'))),
                    filter(agentId => !!agentId)
                ),
                this.curationAgentsService.agentScope$,
                this.dashboardSearchService.getSearchCriteria().pipe(filter(criteria => !!criteria))
            ]).subscribe(
                ([agentId, agentScope, criteria]) => this.applyScope(agentId, agentScope, criteria)
            )
        );
    }

    /**
     * Re-narrows the dashboard. Pushing the criteria re-emits it, so this settles rather than
     * looping: applyAgentScope reports nothing changed on the second pass and it stops there.
     */
    private applyScope(agentId: number, agentScope: AgentScopeModel, criteria: MentionSearchCriteriaModel): void {
        this.applyCoverageInsightsScope(agentId, agentScope);

        if (!applyAgentScope(criteria.query, agentId, agentScope)) {
            return;
        }

        this.dashboardSearchService.setSearchCriteria(criteria);
    }

    /**
     * The dashboard's Coverage Insights tab counts and analyses off MentionSearchService rather
     * than the dashboard's own service, so the agent's scope has to reach that one too - otherwise
     * it reports on the whole account and offers to analyse it.
     */
    private applyCoverageInsightsScope(agentId: number, agentScope: AgentScopeModel): void {
        this.mentionSearchService.getSearchCriteria().pipe(take(1)).subscribe(criteria => {
            const scoped = criteria ?? new MentionSearchCriteriaModel();
            const changed = applyAgentScope(scoped.query, agentId, agentScope);

            if (!changed && criteria?.isAccountSearch) {
                return;
            }

            // Agents act on saved coverage, and this is what picks the account filter list
            scoped.isAccountSearch = true;
            scoped.query.dateQuery = this.appliedRange ?? scoped.query.dateQuery;

            this.mentionSearchService.setSearchCriteria(scoped);
            this.mentionSearchService.search(true, true);
        });
    }

    ngAfterViewInit(): void {
        // Read only, and never echoing the window this component applied: forwards the dashboard's
        // own picker to the other sections
        this.subscriptions.push(
            this.dashboardSearchService.getDateQuery().pipe(
                filter(dateQuery => !!dateQuery && !DateQueryModel.equals(dateQuery, this.appliedRange, false))
            ).subscribe(dateQuery => this.curationAgentsService.setSelectedDateRange(dateQuery))
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }
}
